import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';
import { autoflowColors, autoflowScale } from './autoflow-colors.js';
import bayhiveExtensions from './bayhive/extensions.js';
import collectionLayoutStyles from './bayhive/styles/collection-layout.js';
import formLayoutStyles from './bayhive/styles/form-layout.js';
import compositionUtilityStyles from './bayhive/styles/utilities.js';
import { autoflowFeedbackComponents, autoflowFeedbackSemantic } from './autoflow-feedback.js';

/**
 * Autoflow uses its own blue palette with Lara's component geometry. Keep
 * success and danger independent of the brand color so decisions stay clear.
 *
 * It does host components BayHive owns: the Time Clock widget in the header,
 * the Time Clock page, its settings form on the shop settings page, and the
 * shared user editor. Those are written against BayHive's `--p-bayhive-*`
 * tokens and `.bayhive-*` layout utilities, which the BayHive preset scopes to
 * `html[data-ui-brand='bayhive']`. Re-emit that structural subset under the
 * autoflow brand — the tokens plus the form/collection/utility layout rules,
 * never the primitives, semantics, or component exceptions that carry the
 * BayHive look — so the shared components lay out the same in both shells.
 */
const forAutoflowBrand = (css) =>
  css.replaceAll("html[data-ui-brand='bayhive']", "html[data-ui-brand='autoflow']");

const secondaryButtonColor = `light-dark(${autoflowColors.ink}, ${autoflowColors.white})`;
const secondaryButtonInteraction = {
  color: secondaryButtonColor,
  hoverBackground: 'light-dark({surface.200}, {surface.700})',
  activeBackground: 'light-dark({surface.300}, {surface.600})',
};
const secondaryButtonFilledBackground = 'var(--ink-secondary-color, #adb5bd)';
const secondaryButtonFilledHoverBackground = 'var(--bs-secondary, #6c757d)';
const secondaryButtonFilledActiveBackground =
  'color-mix(in srgb, var(--bs-secondary, #6c757d) 85%, var(--bs-black, #000000))';

export const autoflowPreset = definePreset(Lara, {
  // Cover components that still refer directly to Lara's primitive palettes.
  primitive: Object.fromEntries(
    Object.entries({
      emerald: autoflowColors.success,
      green: autoflowColors.success,
      lime: autoflowColors.success,
      red: autoflowColors.danger,
      orange: 'var(--autoflow-warning-color)',
      amber: autoflowColors.warn,
      yellow: autoflowColors.warn,
      teal: 'var(--bs-teal)',
      cyan: autoflowColors.primary,
      sky: autoflowColors.primary,
      blue: autoflowColors.primary,
      indigo: 'var(--bs-indigo)',
      violet: 'var(--bs-purple)',
      purple: 'var(--bs-purple)',
      fuchsia: 'var(--bs-pink)',
      pink: 'var(--bs-pink)',
      rose: 'var(--bs-pink)',
      slate: 'var(--bs-gray)',
      gray: 'var(--bs-gray)',
      zinc: 'var(--bs-gray)',
      neutral: 'var(--bs-gray)',
      stone: 'var(--bs-gray)',
    }).map(([name, color]) => [name, autoflowScale(color)]),
  ),
  semantic: {
    ...autoflowFeedbackSemantic,
    typography: {
      fontFamily: 'var(--bs-body-font-family, "Inter", "Helvetica Neue", Arial, sans-serif)',
    },
    formField: {
      borderRadius: 'var(--bs-border-radius)',
      invalidBorderColor: '{danger.text.color}',
      invalidPlaceholderColor: '{danger.text.color}',
    },
    content: { borderRadius: 'var(--bs-border-radius)' },
    overlay: {
      select: { borderRadius: 'var(--bs-border-radius)' },
      popover: { borderRadius: 'var(--bs-border-radius)' },
      modal: { borderRadius: 'var(--bs-border-radius-lg)' },
    },
    primary: {
      ...autoflowScale(autoflowColors.primary),
      color: 'light-dark({primary.500}, {primary.300})',
      textColor: 'light-dark({primary.800}, {primary.200})',
      contrastColor: autoflowColors.ink,
      hoverColor: 'light-dark({primary.400}, {primary.200})',
      activeColor: 'light-dark({primary.300}, {primary.100})',
    },
    surface: {
      0: 'var(--bs-white)',
      50: 'var(--bs-gray-100)',
      100: 'var(--bs-gray-100)',
      200: 'var(--bs-gray-200)',
      300: 'var(--bs-gray-300)',
      400: 'var(--bs-gray-400)',
      500: 'var(--bs-gray-500)',
      600: 'var(--bs-gray-600)',
      700: 'var(--bs-gray-700)',
      800: 'var(--bs-gray-800)',
      900: 'var(--bs-gray-900)',
      950: 'var(--bs-black)',
    },
  },
  components: {
    ...autoflowFeedbackComponents,
    card: { root: { borderRadius: 'var(--bs-card-border-radius, 0.25rem)' } },
    panel: {
      header: { borderRadius: 'var(--bs-border-radius) var(--bs-border-radius) 0 0' },
    },
    badge: {
      ...autoflowFeedbackComponents.badge,
      root: { borderRadius: 'var(--bs-border-radius)' },
    },
    button: {
      ...autoflowFeedbackComponents.button,
      root: {
        ...autoflowFeedbackComponents.button.root,
        primary: {
          color: autoflowColors.white,
          hoverColor: autoflowColors.white,
          activeColor: autoflowColors.white,
        },
        secondary: {
          background: secondaryButtonFilledBackground,
          hoverBackground: secondaryButtonFilledHoverBackground,
          activeBackground: secondaryButtonFilledActiveBackground,
          color: autoflowColors.white,
          hoverColor: autoflowColors.white,
          activeColor: autoflowColors.white,
          borderColor: secondaryButtonFilledBackground,
          hoverBorderColor: secondaryButtonFilledHoverBackground,
          activeBorderColor: secondaryButtonFilledActiveBackground,
          focusRing: {
            color: '{primary.text.color}',
            shadow: '0 0 0 0.2rem {primary.200}',
          },
        },
      },
      outlined: {
        ...autoflowFeedbackComponents.button.outlined,
        primary: { color: 'light-dark({primary.800}, {primary.200})' },
        secondary: {
          ...secondaryButtonInteraction,
          borderColor: 'light-dark({surface.400}, {surface.500})',
        },
      },
      text: {
        ...autoflowFeedbackComponents.button.text,
        primary: { color: 'light-dark({primary.800}, {primary.200})' },
        secondary: secondaryButtonInteraction,
      },
      link: {
        color: 'light-dark({primary.800}, {primary.200})',
        hoverColor: 'light-dark({primary.800}, {primary.100})',
        activeColor: 'light-dark({primary.900}, {primary.50})',
      },
    },
  },
  extend: bayhiveExtensions,
  css: (context) =>
    [formLayoutStyles(context), collectionLayoutStyles(context), compositionUtilityStyles(context)]
      .map(forAutoflowBrand)
      .join('\n'),
});

export default autoflowPreset;
